import { useEffect, useState } from "react";
import { Crown, Search, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Empty, Loader } from "@/components/Page";

const FILTERS = [["all", "All"], ["active", "Active"], ["inactive", "Inactive"]];
const dateCell = (value) => value || "Not recorded";

export function VipUsersPanel() {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [query, status]);
  useEffect(() => {
    setData(null);
    const timer = setTimeout(() => {
      api.get("/admin/vip-users", { params: { q: query, status, page } })
        .then((response) => setData(response.data))
        .catch(() => setData(false));
    }, 200);
    return () => clearTimeout(timer);
  }, [query, status, page]);

  return (
    <section data-testid="admin-vip-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Crown size={18} className="text-amber-400" />
          <h2 className="font-cinzel text-base text-amber-300 uppercase tracking-widest">VIP Memberships</h2>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="admin-vip-status-filters">
          {FILTERS.map(([value, label]) => (
            <button key={value} data-testid={`admin-vip-filter-${value}`} onClick={() => setStatus(value)}
              className={`px-3 py-1.5 border text-xs font-cinzel uppercase tracking-wide transition-colors ${status === value ? "border-amber-500 bg-amber-950/30 text-amber-300" : "border-zinc-800 text-zinc-500 hover:text-amber-300"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="relative max-w-md mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input data-testid="admin-vip-search" value={query} onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by name or email…" className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm px-3 py-2 pl-9 outline-none focus:border-amber-600 transition-colors" />
      </div>

      {data === null ? <Loader /> : data === false ? <Empty label="Couldn’t load VIP memberships." /> : data.users.length === 0 ? <Empty label="No VIP membership records found." /> : (
        <>
          <div className="border border-amber-900/30 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-zinc-950 text-[11px] uppercase tracking-widest text-zinc-500 border-b border-amber-900/30">
                <th className="text-left px-4 py-3">Lanista</th><th className="text-left px-4 py-3 hidden sm:table-cell">Email</th>
                <th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3 hidden lg:table-cell">Source</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Last Paid</th><th className="text-left px-4 py-3 hidden xl:table-cell">Became Active</th>
                <th className="text-left px-4 py-3 hidden xl:table-cell">Became Inactive</th>
              </tr></thead>
              <tbody>{data.users.map((member) => (
                <tr key={member.user_id} data-testid={`admin-vip-row-${member.user_id}`} className="border-b border-zinc-900">
                  <td className="px-4 py-3 text-zinc-100" data-testid={`admin-vip-name-${member.user_id}`}>{member.name}</td>
                  <td className="px-4 py-3 text-zinc-500 hidden sm:table-cell" data-testid={`admin-vip-email-${member.user_id}`}>{member.email || "—"}</td>
                  <td className="px-4 py-3" data-testid={`admin-vip-status-${member.user_id}`}>
                    <span className={member.active ? "text-emerald-300" : "text-zinc-400"}>{member.active ? "Active" : `Inactive · ${member.status}`}</span>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 hidden lg:table-cell" data-testid={`admin-vip-source-${member.user_id}`}>{member.membership_source}</td>
                  <td className="px-4 py-3 text-zinc-400 tabular-nums whitespace-nowrap hidden md:table-cell" data-testid={`admin-vip-last-paid-${member.user_id}`}>{dateCell(member.last_paid_at_display)}</td>
                  <td className="px-4 py-3 text-zinc-400 tabular-nums whitespace-nowrap hidden xl:table-cell" data-testid={`admin-vip-active-since-${member.user_id}`}>{dateCell(member.active_since_display)}</td>
                  <td className="px-4 py-3 text-zinc-400 tabular-nums whitespace-nowrap hidden xl:table-cell" data-testid={`admin-vip-inactive-since-${member.user_id}`}>{dateCell(member.inactive_since_display)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4" data-testid="admin-vip-pagination">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 tabular-nums" data-testid="admin-vip-total">{data.total} membership records · {data.server_timezone}</span>
            {data.total_pages > 1 && <div className="flex items-center gap-4">
              <button data-testid="admin-vip-prev" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page <= 1}
                className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-amber-300 disabled:opacity-30 transition-colors"><ChevronLeft size={13} /> Prev</button>
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 tabular-nums" data-testid="admin-vip-page-indicator">Page {data.page} of {data.total_pages}</span>
              <button data-testid="admin-vip-next" onClick={() => setPage((current) => Math.min(data.total_pages, current + 1))} disabled={page >= data.total_pages}
                className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-amber-300 disabled:opacity-30 transition-colors">Next <ChevronRight size={13} /></button>
            </div>}
          </div>
        </>
      )}
    </section>
  );
}